import React from 'react';
import { cn } from '../../lib/utils';
import { UploadZone } from '../sidebar/UploadZone';
import { SettingsPanel } from '../sidebar/SettingsPanel';
import { FileList } from '../sidebar/FileList';
import { ActionButtons } from '../sidebar/ActionButtons';
import { UploadedFile } from '../../types/detection';
import { SIDEBAR_CLASSES } from '../../constants/layout';

interface AppSidebarProps {
  // Upload zone props
  isDragging: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onBrowseClick: () => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  allowedFormats: string[];
  // Settings props
  confThreshold: number;
  onConfThresholdChange: (value: number) => void;
  pixelToUm: number;
  onPixelToUmChange: (value: number) => void;
  // File list props
  files: UploadedFile[];
  onRemoveFile: (id: string) => void;
  onClearAll: () => void;
  onFileClick: (id: string) => void;
  activeFileId?: string;
  formatFileSize: (bytes: number) => string;
  // Action button props
  isProcessing: boolean;
  processingProgress: number;
  pendingCount: number;
  hasResults: boolean;
  onProcess: () => void;
  onCancel: () => void;
  onExport: () => void;
  // Container props
  className?: string;
}

export const AppSidebar: React.FC<AppSidebarProps> = ({
  // Upload zone props
  isDragging,
  onDragOver,
  onDragLeave,
  onDrop,
  onBrowseClick,
  fileInputRef,
  onFileInputChange,
  allowedFormats,
  // Settings props
  confThreshold,
  onConfThresholdChange,
  pixelToUm,
  onPixelToUmChange,
  // File list props
  files,
  onRemoveFile,
  onClearAll,
  onFileClick,
  activeFileId,
  formatFileSize,
  // Action button props
  isProcessing,
  processingProgress,
  pendingCount,
  hasResults,
  onProcess,
  onCancel,
  onExport,
  // Container props
  className,
}) => {
  return (
    <aside
      className={cn(
        "fixed left-0 top-[85px] bottom-0 bg-white dark:bg-gray-900 border-r dark:border-gray-800 shadow-sm z-30",
        "hidden md:flex flex-col overflow-hidden",
        SIDEBAR_CLASSES.width,
        className
      )}
    >
      {/* Fixed top section - Settings first, then Upload */}
      <div className="flex-shrink-0 p-4 space-y-4">
        <SettingsPanel
          confThreshold={confThreshold}
          onConfThresholdChange={onConfThresholdChange}
          pixelToUm={pixelToUm}
          onPixelToUmChange={onPixelToUmChange}
        />

        <UploadZone
          isDragging={isDragging}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onBrowseClick={onBrowseClick}
          fileInputRef={fileInputRef}
          onFileInputChange={onFileInputChange}
          allowedFormats={allowedFormats}
        />
      </div>

      {/* Scrollable file list - takes remaining space */}
      <div className="flex-1 overflow-y-auto px-4 min-h-0">
        <FileList
          files={files}
          onRemoveFile={onRemoveFile}
          onClearAll={onClearAll}
          onFileClick={onFileClick}
          activeFileId={activeFileId}
          isProcessing={isProcessing}
          formatFileSize={formatFileSize}
        />
      </div>

      {/* Sticky action buttons at bottom */}
      <ActionButtons
        isProcessing={isProcessing}
        processingProgress={processingProgress}
        pendingCount={pendingCount}
        hasResults={hasResults}
        onProcess={onProcess}
        onCancel={onCancel}
        onExport={onExport}
      />
    </aside>
  );
};
